import React from 'react';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { AlertTriangle, MessageSquare, DollarSign, Wrench, Users } from 'lucide-react';

interface PainPointsAnalysisProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_3_PainPointsAnalysis: React.FC<PainPointsAnalysisProps> = ({
  data,
  onUpdate,
}) => {
  const updateChallenges = (challenge: string, checked: boolean) => {
    const currentChallenges = data.biggestChallenges || [];
    const updated = checked
      ? [...currentChallenges, challenge]
      : currentChallenges.filter((c) => c !== challenge);
    onUpdate({ ...data, biggestChallenges: updated });
  };

  const updateCommunicationIssues = (issue: string, checked: boolean) => {
    const currentIssues = data.communicationIssues || [];
    const updated = checked ? [...currentIssues, issue] : currentIssues.filter((i) => i !== issue);
    onUpdate({ ...data, communicationIssues: updated });
  };

  const updateFinancialConcerns = (concern: string, checked: boolean) => {
    const currentConcerns = data.financialConcerns || [];
    const updated = checked
      ? [...currentConcerns, concern]
      : currentConcerns.filter((c) => c !== concern);
    onUpdate({ ...data, financialConcerns: updated });
  };

  const updateMaintenanceProblems = (problem: string, checked: boolean) => {
    const currentProblems = data.maintenanceProblems || [];
    const updated = checked
      ? [...currentProblems, problem]
      : currentProblems.filter((p) => p !== problem);
    onUpdate({ ...data, maintenanceProblems: updated });
  };

  const updateResidentSatisfaction = (issue: string, checked: boolean) => {
    const currentIssues = data.residentSatisfactionIssues || [];
    const updated = checked ? [...currentIssues, issue] : currentIssues.filter((i) => i !== issue);
    onUpdate({ ...data, residentSatisfactionIssues: updated });
  };

  const biggestChallengeOptions = [
    'Board meeting attendance is low',
    'Difficult to get community consensus on decisions',
    'Voting processes are slow or disputed',
    'Financial transparency concerns',
    'Poor communication between board and residents',
    'Maintenance requests take too long',
    'Budget planning is challenging',
    'Vendor management issues',
    'Resident compliance problems',
    'Emergency response coordination',
    'Insurance claim management',
    'Legal dispute resolution',
  ];

  const communicationIssueOptions = [
    "Residents don't read emails or newsletters",
    'No centralized communication platform',
    'Important information gets lost',
    'Language barriers in the community',
    'Different generations prefer different communication methods',
    'Board decisions are not well communicated',
    'Urgent notifications are hard to send',
    'Meeting minutes are hard to distribute',
  ];

  const financialConcernOptions = [
    'Treasury is not generating meaningful returns',
    'Budget planning is reactive, not proactive',
    'Difficulty tracking expenses across categories',
    'No visibility into financial performance',
    'Special assessments are frequently needed',
    'Cash flow management challenges',
    'Vendor payment processing issues',
    'No long-term financial planning',
  ];

  const maintenanceIssueOptions = [
    'Work order tracking is manual or non-existent',
    "Residents can't easily submit requests",
    'No visibility into maintenance status',
    'Vendor coordination is challenging',
    'Preventive maintenance is neglected',
    'Emergency repairs are poorly managed',
    'Quality control of completed work',
    'Maintenance budget overruns',
  ];

  const residentSatisfactionOptions = [
    'Low participation in community activities',
    'Complaints about board transparency',
    'Disputes between neighbors',
    'Dissatisfaction with maintenance response times',
    'Feeling disconnected from community decisions',
    'Concerns about property values',
    'Inadequate enforcement of community rules',
    'Limited social activities or events',
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Current Challenges & Pain Points
        </h3>
        <p className="text-gray-600">
          Understanding your current challenges helps us prioritize features and customize your
          implementation
        </p>
      </div>

      <div className="space-y-6">
        {/* Biggest Challenges */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Biggest Operational Challenges
            </CardTitle>
            <CardDescription>
              What are the most pressing issues your community faces? (Select all that apply)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {biggestChallengeOptions.map((challenge) => (
                <div key={challenge} className="flex items-center space-x-2">
                  <Checkbox
                    id={`challenge-${challenge}`}
                    checked={data.biggestChallenges?.includes(challenge) || false}
                    onCheckedChange={(checked) => updateChallenges(challenge, checked as boolean)}
                  />
                  <Label htmlFor={`challenge-${challenge}`} className="text-sm">
                    {challenge}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Communication Issues */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Communication Challenges
            </CardTitle>
            <CardDescription>
              How does information flow (or fail to flow) in your community?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {communicationIssueOptions.map((issue) => (
                <div key={issue} className="flex items-center space-x-2">
                  <Checkbox
                    id={`comm-${issue}`}
                    checked={data.communicationIssues?.includes(issue) || false}
                    onCheckedChange={(checked) =>
                      updateCommunicationIssues(issue, checked as boolean)
                    }
                  />
                  <Label htmlFor={`comm-${issue}`} className="text-sm">
                    {issue}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Financial Concerns */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Financial Management Issues
            </CardTitle>
            <CardDescription>What financial challenges does your community face?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {financialConcernOptions.map((concern) => (
                <div key={concern} className="flex items-center space-x-2">
                  <Checkbox
                    id={`financial-${concern}`}
                    checked={data.financialConcerns?.includes(concern) || false}
                    onCheckedChange={(checked) =>
                      updateFinancialConcerns(concern, checked as boolean)
                    }
                  />
                  <Label htmlFor={`financial-${concern}`} className="text-sm">
                    {concern}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Problems */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Maintenance & Operations
            </CardTitle>
            <CardDescription>
              What maintenance and operational issues need attention?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {maintenanceIssueOptions.map((problem) => (
                <div key={problem} className="flex items-center space-x-2">
                  <Checkbox
                    id={`maintenance-${problem}`}
                    checked={data.maintenanceProblems?.includes(problem) || false}
                    onCheckedChange={(checked) =>
                      updateMaintenanceProblems(problem, checked as boolean)
                    }
                  />
                  <Label htmlFor={`maintenance-${problem}`} className="text-sm">
                    {problem}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Resident Satisfaction */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Resident Satisfaction Issues
            </CardTitle>
            <CardDescription>
              What concerns do residents express about community life?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {residentSatisfactionOptions.map((issue) => (
                <div key={issue} className="flex items-center space-x-2">
                  <Checkbox
                    id={`satisfaction-${issue}`}
                    checked={data.residentSatisfactionIssues?.includes(issue) || false}
                    onCheckedChange={(checked) =>
                      updateResidentSatisfaction(issue, checked as boolean)
                    }
                  />
                  <Label htmlFor={`satisfaction-${issue}`} className="text-sm">
                    {issue}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Current Governance Structure */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Current Governance Structure</CardTitle>
            <CardDescription>
              Describe how decisions are currently made in your community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="governanceStructure">
                How does your community currently make decisions?
              </Label>
              <Textarea
                id="governanceStructure"
                value={data.currentGovernanceStructure || ''}
                onChange={(e) => onUpdate({ ...data, currentGovernanceStructure: e.target.value })}
                placeholder="e.g., Board of 5 members meets monthly, decisions require majority vote, annual meeting for major items..."
                rows={4}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Priority Insight */}
      {(data.biggestChallenges?.length || 0) > 0 && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h4 className="font-semibold text-orange-800 mb-2">Priority Focus Areas</h4>
              <p className="text-sm text-orange-700">
                Based on your selections, we'll prioritize features that address your most pressing
                challenges:
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-3">
                {data.biggestChallenges?.slice(0, 3).map((challenge) => (
                  <span
                    key={challenge}
                    className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs"
                  >
                    {challenge}
                  </span>
                ))}
                {(data.biggestChallenges?.length || 0) > 3 && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                    +{(data.biggestChallenges?.length || 0) - 3} more
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>How this helps:</strong> Understanding your pain points allows us to:
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Prioritize features that solve your most pressing problems first</li>
          <li>• Customize the implementation timeline to address urgent issues quickly</li>
          <li>• Provide specific training and support for your biggest challenges</li>
          <li>• Demonstrate clear before/after improvements during your discovery call</li>
        </ul>
      </div>
    </div>
  );
};
